/**
 * Dialog Appearance Registry
 *
 * Coordinated visual styles for dialog chrome (header, content, footer).
 * The "appearance" is the ONE LEVER that controls how a dialog looks.
 *
 * Usage:
 *   import { StyledDialog } from '@/components/dialogs';
 *
 *   <StyledDialog
 *     title="Edit Profile"
 *     footer={{ variant: "confirm", onPrimary: handleSave }}
 *   >
 *     {content}
 *   </StyledDialog>
 *
 * All dialogs use the same structure (header + content + footer).
 * The appearance controls how each section is styled.
 */

import { vars } from '@/theme/tokens.css';

// =============================================================================
// APPEARANCE CONFIGURATION TYPES
// =============================================================================

export interface DialogAppearanceConfig {
  /** Close button styling */
  header: {
    /** CSS color for the close button icon */
    color: string;
    /** If true, close button gets a circular dark overlay background (minimal only) */
    isOverlay: boolean;
    /** If true, no close button renders at all (compact: Cancel is the way out) */
    hideClose?: boolean;
  };
  /** Content area configuration */
  content: {
    /** If true, extra top padding is added to clear the absolute close button */
    hasExtraTopPadding: boolean;
    /** If true, content has no inner padding (minimal appearance for media) */
    zeroInnerPadding: boolean;
  };
  /** Footer configuration */
  footer: {
    /** Whether the standard footer (with action buttons) is visible */
    visible: boolean;
  };
  /** Dialog paper (root container) configuration */
  paper: {
    /** CSS max-height constraint */
    maxHeight?: string;
    /** CSS border-radius (e.g. '16px') */
    borderRadius?: string;
    /** Whether to clip overflow (e.g. 'hidden' for media dialogs) */
    overflow?: 'hidden';
  };
  /** Whether title renders as first element in content area (vs. in a header bar) */
  titleInContent: boolean;
  /** Backdrop behind the dialog: 'standard' = overlays.light (50%), 'dim' = overlays.dim (25%) */
  scrim?: 'dim';
  /** If true the dialog is content-sized (min 300, max 444, 20px padding) instead of a size preset */
  contentSized?: boolean;
}

// =============================================================================
// APPEARANCE DEFINITIONS
// =============================================================================

/**
 * Dialog appearance presets.
 *
 * | Appearance | Header | Footer | Use Case |
 * |------------|--------|--------|----------|
 * | clean | Floating close only | Inline with content | Most dialogs (DEFAULT) |
 * | minimal | Overlay close | Hidden | Lightbox, QR code |
 * | compact | None | Inline, 20px padding | One-sentence confirm (opt-in) |
 */
export const dialogAppearance = {
  /**
   * Clean appearance - Minimal chrome, content-focused (DEFAULT).
   * No colored header, just floating close button. Footer inline with content.
   * Title renders as first element in content area.
   *
   * Use for: All standard dialogs — confirmations, forms, detail views, settings.
   */
  clean: {
    header: {
      color: vars.color.neutral.base,
      isOverlay: false,
    },
    content: {
      hasExtraTopPadding: true,
      zeroInnerPadding: false,
    },
    footer: {
      visible: true,
    },
    paper: {
      maxHeight: '90vh',
    },
    titleInContent: true,
  } satisfies DialogAppearanceConfig,

  /**
   * Minimal appearance - Just content with overlay close.
   * No visible header/footer. Close button overlays content.
   *
   * Use for: Image lightbox, QR codes, media viewers.
   * Examples: QrCodeDialog, image lightbox
   */
  minimal: {
    header: {
      color: vars.color.fixed.white,
      isOverlay: true,
    },
    content: {
      hasExtraTopPadding: false,
      zeroInnerPadding: true,
    },
    footer: {
      visible: false,
    },
    paper: {
      borderRadius: vars.radius.none,
      overflow: 'hidden',
    },
    titleInContent: false,
  } satisfies DialogAppearanceConfig,

  /**
   * Compact appearance - a small content-sized prompt (Q-530).
   * No close button, no visible title (the accessible name is the message),
   * 25% scrim. Cancel, Esc and a click outside are the ways out.
   *
   * Use for: a one-sentence confirm opted into via ConfirmDialog appearance="compact".
   */
  compact: {
    header: {
      color: vars.color.neutral.base,
      isOverlay: false,
      hideClose: true,
    },
    content: {
      hasExtraTopPadding: false,
      zeroInnerPadding: false,
    },
    footer: {
      visible: true,
    },
    paper: {
      maxHeight: '90vh',
    },
    titleInContent: false,
    scrim: 'dim',
    contentSized: true,
  } satisfies DialogAppearanceConfig,
} as const;

export type DialogAppearance = keyof typeof dialogAppearance;

/**
 * Get appearance config by name.
 */
export function getDialogAppearance(appearance: DialogAppearance): DialogAppearanceConfig {
  return dialogAppearance[appearance];
}
